import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../services/api';
import socket from '../../services/socket';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// 🚀 CONECTADO AL CONTEXTO GLOBAL DE AUTENTICACIÓN
import { useAuth } from '../../context/AuthContext';

// Mantenemos solo los iconos puros
import {
    Send, Campaign, Close, PriorityHigh,
    InfoOutlined, DeleteOutlined
} from '@mui/icons-material';

// =====================================================================
// 🧩 SUB-COMPONENTE 1: FORMULARIO DE REDACCIÓN
// =====================================================================
const FormularioNota = ({ onPublicar }) => {
    const [mensaje, setMensaje] = useState('');
    const [esUrgente, setEsUrgente] = useState(false);

    const handleSubmit = () => {
        if (!mensaje.trim()) return;
        onPublicar(mensaje.trim(), esUrgente);
        setMensaje('');
        setEsUrgente(false);
    };

    return (
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl mb-6">
            <textarea
                aria-label="Redactar nuevo aviso"
                className="w-full p-3 bg-white border border-slate-200 rounded-xl text-slate-700 text-sm font-medium focus:outline-none focus:border-clinica-primary focus:ring-1 focus:ring-clinica-primary transition-all resize-none mb-3"
                rows="2"
                placeholder="Redactar nuevo aviso (ej. Falta material en la sala 3)..."
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
            />
            <div className="flex justify-between items-center">
                <button
                    onClick={() => setEsUrgente(!esUrgente)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors focus:outline-none ${
                        esUrgente
                            ? 'bg-red-500 text-white shadow-sm'
                            : 'bg-white text-red-500 border border-red-200 hover:bg-red-50'
                    }`}
                >
                    <PriorityHigh sx={{ fontSize: 16 }} />
                    {esUrgente ? 'Prioridad: Crítica' : 'Marcar como Urgente'}
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={!mensaje.trim()}
                    className="flex items-center gap-2 px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-xl transition-colors shadow-sm focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Publicar <Send fontSize="small" />
                </button>
            </div>
        </div>
    );
};

// =====================================================================
// 🧩 SUB-COMPONENTE 2: TARJETA DE AVISO INDIVIDUAL
// =====================================================================
const TarjetaNota = ({ nota, user, onBorrar }) => {
    const emisorRol = nota.emisor?.rol || nota.emisor?.role || 'medico';
    const esDueno = nota.emisor?.id === user?.id || nota.emisor?._id === user?.id;
    const esSuperMedico = user?.role === 'supermedico' || user?.rol === 'supermedico';
    const puedeEliminar = esDueno || esSuperMedico;

    const inicialNombre = nota.emisor?.nombre?.charAt(0) || '';
    const inicialApellido = nota.emisor?.apellidos?.charAt(0) || '';
    const iniciales = (inicialNombre + inicialApellido).toUpperCase() || 'U';

    return (
        <div className={`relative flex flex-col justify-between p-5 rounded-2xl border min-h-[140px] transition-all hover:shadow-md ${
            nota.urgente
                ? 'bg-red-50 border-red-200'
                : 'bg-white border-slate-200'
        }`}>
            <div>
                <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                        <div className={`flex items-center justify-center w-10 h-10 rounded-xl text-white font-black text-sm shadow-sm ${
                            nota.urgente ? 'bg-red-500' : 'bg-slate-600'
                        }`}>
                            {iniciales}
                        </div>
                        <div>
                            <h4 className="text-sm font-black text-slate-900 leading-tight">
                                {nota.emisor?.nombre ? `${nota.emisor.nombre} ${nota.emisor.apellidos || ''}` : 'Anónimo'}
                            </h4>
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                                {emisorRol === 'supermedico' ? 'Director Médico' : emisorRol}
                            </p>
                        </div>
                    </div>

                    {nota.urgente && (
                        <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-black tracking-widest rounded-md uppercase">
                            Urgente
                        </span>
                    )}
                </div>

                <p className="text-sm font-medium text-slate-700 whitespace-pre-wrap mb-4">
                    {nota.contenido}
                </p>
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-slate-900/5">
                <span className="text-xs font-bold text-slate-400">
                    {nota.fechaCreacion ? format(new Date(nota.fechaCreacion), "dd MMM, HH:mm'h'", { locale: es }) : '--/--'}
                </span>

                {puedeEliminar && (
                    <button
                        onClick={() => onBorrar(nota._id || nota.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-100 rounded-lg transition-colors focus:outline-none"
                        title="Archivar Aviso"
                    >
                        <DeleteOutlined fontSize="small" />
                    </button>
                )}
            </div>
        </div>
    );
};

// =====================================================================
// 🏢 COMPONENTE CONTENEDOR PRINCIPAL: COMUNICACIÓN INTERNA
// =====================================================================
const ComunicacionInterna = ({ open, onClose }) => {
    const [tabIndex, setTabIndex] = useState(0);
    const [notas, setNotas] = useState([]);

    // 🚀 EXTRACCIÓN LIMPIA DESDE EL AUTOCONTEXT REFACTURADO
    const { user } = useAuth();

    const cargarNotasTablon = useCallback(async () => {
        try {
            const rawRes = await api.get('/notas');

            // 🚀 BLINDAJE 1: Quitamos la capa de Axios primero
            const payload = rawRes?.data || rawRes;

            // 🚀 BLINDAJE 2: Buscamos el array dentro del contenido limpio
            const datosNotas = Array.isArray(payload) ? payload : (payload?.docs || payload?.data || []);

            setNotas(datosNotas);
        } catch (err) {
            console.error("Error al sincronizar el tablón de anuncios:", err);
            setNotas([]);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        cargarNotasTablon();

        // Aseguramos la conexión limpia del canal reactivo
        if (!socket.connected) socket.connect();

        const handleNuevaNota = (nuevaNota) => {
            // Sockets: Alguien ha publicado. Lo metemos en nuestra pantalla sin recargar.
            // 🛡️ FIX: el autor también recibe su propio broadcast (está en la sala
            // 'clinica_global') y ya la había añadido al publicar → salía duplicada.
            const idNueva = nuevaNota?.id || nuevaNota?._id;
            setNotas(prev => (idNueva && prev.some(n => (n.id || n._id) === idNueva)) ? prev : [nuevaNota, ...prev]);
        };

        const handleNotaEliminada = (notaId) => {
            // Sockets: Alguien la ha borrado. La quitamos de nuestra pantalla al instante.
            setNotas(prev => prev.filter(n => n.id !== notaId && n._id !== notaId));
        };

        socket.on('nueva_nota_global', handleNuevaNota);
        socket.on('nota_global_eliminada', handleNotaEliminada);

        return () => {
            socket.off('nueva_nota_global', handleNuevaNota);
            socket.off('nota_global_eliminada', handleNotaEliminada);
        };
    }, [open, cargarNotasTablon]);

    const handlePublicar = async (contenido, urgente) => {
        try {
            const response = await api.post('/notas', { contenido, urgente });
            const notaCreada = response.data || response;

            // 🚀 MAGIA REACTIVA (Crear): Inyectamos la nota en el array para no tener que refrescar
            if (notaCreada && (notaCreada.id || notaCreada._id)) {
                const idCreada = notaCreada.id || notaCreada._id;
                setNotas(prevNotas => prevNotas.some(n => (n.id || n._id) === idCreada) ? prevNotas : [notaCreada, ...prevNotas]);
            } else {
                cargarNotasTablon(); // Fallback si no llega el objeto completo
            }

        } catch (err) {
            alert(err.message || 'Error al emitir el comunicado');
        }
    };

    const handleBorrar = async (id) => {
        try {
            // Le pedimos al backend que borre en la BBDD
            await api.delete(`/notas/${id}`);

            // 🚀 MAGIA REACTIVA (Borrar): Eliminamos la nota de nuestro array local al instante
            // Filtramos la nota que tenga ese id, por lo que React "destruirá" la tarjeta visualmente.
            setNotas(prevNotas => prevNotas.filter(n => n.id !== id && n._id !== id));

        } catch (err) {
            alert(err.message || 'No posee los permisos necesarios para archivar');
        }
    };

    const notasFiltradas = useMemo(() => {
        if (tabIndex === 1) return notas.filter(n => n.urgente);
        return notas;
    }, [notas, tabIndex]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
            <div className="flex flex-col w-full max-w-4xl h-[85vh] bg-slate-100 rounded-3xl shadow-2xl overflow-hidden">

                {/* CABECERA */}
                <div className="flex items-center justify-between p-6 bg-slate-900 text-white">
                    <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-800 rounded-xl">
                            <Campaign fontSize="large" className="text-sky-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black tracking-tight">Tablón de Comunicación Interna</h2>
                            <p className="text-xs font-semibold text-slate-400 mt-1">Avisos y notificaciones en tiempo real del personal médico</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors focus:outline-none"
                    >
                        <Close />
                    </button>
                </div>

                {/* CONTENIDO PRINCIPAL */}
                <div className="flex flex-col flex-1 overflow-hidden">
                    <div className="p-6 bg-white border-b border-slate-200">
                        <FormularioNota onPublicar={handlePublicar} />

                        {/* PESTAÑAS */}
                        <div className="flex space-x-6 border-b border-slate-200" role="tablist">
                            <button
                                role="tab"
                                aria-selected={tabIndex === 0}
                                onClick={() => setTabIndex(0)}
                                className={`pb-3 text-sm font-bold transition-colors relative focus:outline-none ${
                                    tabIndex === 0 ? 'text-clinica-primary' : 'text-slate-500 hover:text-slate-800'
                                }`}
                            >
                                Todos los avisos ({notas.length})
                                {tabIndex === 0 && (
                                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-clinica-primary rounded-t-full"></span>
                                )}
                            </button>
                            <button
                                role="tab"
                                aria-selected={tabIndex === 1}
                                onClick={() => setTabIndex(1)}
                                className={`flex items-center gap-1.5 pb-3 text-sm font-bold transition-colors relative focus:outline-none ${
                                    tabIndex === 1 ? 'text-red-500' : 'text-slate-500 hover:text-slate-800'
                                }`}
                            >
                                <PriorityHigh sx={{ fontSize: 16 }} /> Urgentes ({notas.filter(n => n.urgente).length})
                                {tabIndex === 1 && (
                                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-red-500 rounded-t-full"></span>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* REJILLA DE TARJETAS */}
                    <div className="p-6 flex-1 overflow-y-auto">
                        {notasFiltradas.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400">
                                <InfoOutlined sx={{ fontSize: 60, mb: 2 }} className="opacity-50" />
                                <h3 className="text-lg font-bold">No hay avisos activos</h3>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {notasFiltradas.map((nota) => (
                                    <TarjetaNota key={nota._id || nota.id} nota={nota} user={user} onBorrar={handleBorrar} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ComunicacionInterna;